export enum TaskStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum AgentType {
  AGENT_128 = 'agent_128',  // General-purpose agent
  RIDER = 'rider',          // 5-layer hierarchical orchestration
  HUSARZ = 'husarz',        // Advanced verification system
}

// Agent type display names and descriptions
export const AGENT_TYPE_INFO: Record<AgentType, { name: string; description: string }> = {
  [AgentType.AGENT_128]: {
    name: 'Agent 128',
    description: 'General-purpose agent for standard tasks',
  },
  [AgentType.RIDER]: {
    name: 'RIDER',
    description: 'Complex multi-step tasks with 5-layer hierarchical orchestration',
  },
  [AgentType.HUSARZ]: {
    name: 'Husarz',
    description: 'Safety-critical tasks with advanced verification',
  },
};

export enum FileType {
  INPUT = 'input',
  OUTPUT = 'output',
}

export interface TaskFile {
  id: number;
  task_id: number;
  iteration_id: number | null;
  file_name: string;
  file_type: FileType;
  file_size: number;
  uploaded_at: string;
}

export interface TaskIteration {
  id: number;
  task_id: number;
  iteration_number: number;
  user_prompt: string | null;
  agent_report: string | null;
  status: TaskStatus;
  created_at: string;
  completed_at: string | null;
  output_files: TaskFile[];
}

export interface Task {
  id: number;
  title: string;
  description: string;
  status: TaskStatus;
  agent_type: AgentType;
  unread: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  iterations: TaskIteration[];
  files: TaskFile[];
  knowledge_bases?: Array<{
    id: number;
    title: string;
    is_default: boolean;
    resource_count: number;
  }>;
}

export interface TaskTagSummary {
  id: number;
  name: string;
  color: string | null;
  full_path: string;
  user_id: number;
  is_own_tag: boolean;
}

export interface TaskTag {
  id: number;
  name: string;
  parent_id: number | null;
  user_id: number;
  color: string | null;
  created_at: string;
  updated_at: string;
  task_count: number;
}

export interface TaskTagTreeNode {
  id: number;
  name: string;
  parent_id: number | null;
  color: string | null;
  task_count: number;
  children: TaskTagTreeNode[];
}

export interface TaskListItem {
  id: number;
  title: string;
  description: string;
  status: TaskStatus;
  agent_type: AgentType;
  unread: boolean;
  archived: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  created_by_username?: string;
  created_by_fullname?: string;
  project_name?: string;
  tags?: TaskTagSummary[];
}

export interface TaskCreateRequest {
  title: string;
  description: string;
  knowledge_base_ids?: number[];
  is_draft?: boolean;
  project_id?: number | null;
  agent_type?: AgentType;  // Default is AGENT_128
}

export interface TaskIterationCreateRequest {
  user_prompt: string;
}

// Progress Observer types (RIDER & Husarz only - Agent 128 does not have progress updates)
export interface ProgressTaskItem {
  content: string;
  completed: boolean;
}

export interface ProgressStatus {
  task_list: ProgressTaskItem[];
  progress_percent: number;  // 0-100, can go backward
  progress_eta: string | null;  // e.g., "~5 minutes" or null
  status_description: string;  // ~320 chars, current activity
}

// Check if agent type supports progress observer
export const hasProgressObserver = (agentType: AgentType): boolean => {
  return agentType === AgentType.RIDER || agentType === AgentType.HUSARZ;
};
